import React from "react";

/* ─── Shared cell style objects ─────────────────────────────────── */
export const cellStyle = {
  padding: "0.7rem 1rem",
  fontSize: "0.8rem",
  color: "var(--color-text-primary)",
  borderBottom: "1px solid #F1F5F9",
  whiteSpace: "nowrap",
  verticalAlign: "middle",
};

export const theadCellStyle = {
  padding: "0.6rem 1rem",
  fontSize: "0.68rem",
  fontWeight: 700,
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  color: "var(--color-text-muted)",
  background: "#F8FAFC",
  borderBottom: "1px solid var(--color-border)",
  whiteSpace: "nowrap",
  textAlign: "left",
};

/* ─── Filter Input Styling (Retailer Theme) ────────────────────────── */
export const filterInputSx = {
  "& .MuiInputBase-root": { height: "40px", backgroundColor: "#F8FAFC", borderRadius: "0.5rem" },
  "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "#E2E8F0" } },
  "& .MuiOutlinedInput-root:hover": { "& fieldset": { borderColor: "#CBD5E1" } },
  "& .MuiOutlinedInput-root.Mui-focused": { "& fieldset": { borderColor: "#4F46E5" } }
};

export const filterSelectSx = {
  height: "40px",
  backgroundColor: "#F8FAFC",
  borderRadius: "0.5rem",
  "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "#E2E8F0" } },
  "& .MuiOutlinedInput-root:hover": { "& fieldset": { borderColor: "#CBD5E1" } },
  "& .MuiOutlinedInput-root.Mui-focused": { "& fieldset": { borderColor: "#4F46E5" } }
};

export const filterFormControlSx = {
  "& .MuiInputLabel-root": { top: "-8px" }
};

/* ─── DataTable ─────────────────────────────────────────────────── */
export function DataTable({ headers, children }) {
  if (headers && headers.length > 0) {
    return (
      <div className="w-full overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {headers.map((h, i) => (
                <th key={i} style={theadCellStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    );
  }
  return (
    <div className="w-full overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
      <table className="w-full border-collapse min-w-[600px]">
        {children}
      </table>
    </div>
  );
}

/* ─── DataRow ───────────────────────────────────────────────────── */
export function DataRow({ children, onClick }) {
  return (
    <tr
      onClick={onClick}
      style={{ transition: "background 100ms" }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "#F8FAFC")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      {children}
    </tr>
  );
}

/* ─── Cell ──────────────────────────────────────────────────────── */
export function Cell({
  children,
  value,
  primary,
  secondary,
  bold,
  center,
  muted,
  extra,
  variant,
  align,
}) {
  const isHead = variant === "head";
  const isIndex = variant === "index";

  const Component = isHead ? "th" : "td";
  const baseStyle = isHead ? theadCellStyle : cellStyle;

  let content = children ?? value;

  if (primary || secondary) {
    content = (
      <div className="flex flex-col" style={{ lineHeight: 1.2, gap: "1px" }}>
        {primary && (
          <span
            className="font-semibold block"
            style={{ color: "var(--color-text-primary)", fontSize: "0.8rem" }}
          >
            {primary}
          </span>
        )}
        {secondary && (
          <span
            className="block"
            style={{ color: "var(--color-text-muted)", fontSize: "0.7rem" }}
          >
            {secondary}
          </span>
        )}
      </div>
    );
  }

  return (
    <Component
      style={{
        ...baseStyle,
        fontWeight: bold ? 600 : isHead ? 700 : 400,
        textAlign: align || (center ? "center" : "left"),
        color: muted
          ? "var(--color-text-muted)"
          : isHead
          ? "var(--color-text-muted)"
          : "var(--color-text-primary)",
        width: isIndex ? 48 : undefined,
        ...extra,
      }}
    >
      {content}
    </Component>
  );
}

/* ─── IndexCell ─────────────────────────────────────────────────── */
export function IndexCell({ value }) {
  return (
    <td style={{ ...cellStyle, color: "var(--color-text-muted)", width: 48, textAlign: "center" }}>
      {value}
    </td>
  );
}

/* ─── StatusBadge ───────────────────────────────────────────────── */
export function StatusBadge({ active, status, label, children, trueLabel = "Active", falseLabel = "Inactive" }) {
  const content = children || label || status || (active ? trueLabel : falseLabel);
  
  // Normalize status for color coding
  const s = (status || "").toString().toLowerCase();
  const isActive = active === true || ["active", "true", "sell_in", "activated", "success"].includes(s);
  const isDanger = active === false || ["inactive", "false", "sell_out", "error", "expired", "deleted"].includes(s);
  
  let bg = "#F8FAFC";
  let color = "#64748B";
  let dot = "#94A3B8";

  if (isActive) {
    bg = "#ECFDF5";
    color = "#059669";
    dot = "#10B981";
  } else if (isDanger) {
    bg = "#FEF2F2";
    color = "#DC2626";
    dot = "#EF4444";
  }

  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
      style={{ background: bg, color: color, border: `1px solid ${isActive ? "#D1FAE5" : isDanger ? "#FEE2E2" : "#E2E8F0"}` }}
    >
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: dot }} />
      {content}
    </span>
  );
}

/* ─── Toolbar ───────────────────────────────────────────────────── */
export function Toolbar({ children }) {
  return (
    <div
      className="flex flex-col sm:flex-row gap-3 p-3 md:p-5 flex-wrap"
      style={{ borderBottom: "1px solid var(--color-border)" }}
    >
      {children}
    </div>
  );
}

/* ─── SearchInput ───────────────────────────────────────────────── */
export function SearchInput({ placeholder = "Search…", value, onChange, style }) {
  return (
    <div className="relative w-full sm:flex-1 sm:min-w-[240px]">
      <span
        className="absolute inset-y-0 left-3 flex items-center pointer-events-none"
        style={{ color: "var(--color-text-muted)" }}
      >
        <i className="fas fa-search" style={{ fontSize: 12 }} />
      </span>
      <input
        type="text"
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full rounded-lg text-sm outline-none transition-all"
        style={{
          padding: "0.5rem 0.875rem 0.5rem 2.25rem",
          border: "1px solid var(--color-border)",
          background: "#F8FAFC",
          color: "var(--color-text-primary)",
          height: 40,
          ...style,
        }}
        onFocus={(e) => (e.target.style.borderColor = "#4F46E5")}
        onBlur={(e) => (e.target.style.borderColor = "var(--color-border)")}
      />
    </div>
  );
}

/* ─── ActionButton ──────────────────────────────────────────────── */
const BTN_STYLES = {
  primary:   { bg: "#4F46E5", hover: "#4338CA", text: "#fff",     border: "#4F46E5" },
  secondary: { bg: "#fff",    hover: "#EEF2FF", text: "#4F46E5",  border: "#C7D2FE" },
  danger:    { bg: "#fff",    hover: "#FEF2F2", text: "#DC2626",  border: "#FECACA" },
  success:   { bg: "#059669", hover: "#047857", text: "#fff",     border: "#059669" },
  ghost:     { bg: "#F8FAFC", hover: "#F1F5F9", text: "#64748B",  border: "#E2E8F0" },
};

export function ActionButton({
  children,
  onClick,
  variant = "primary",
  size = "medium",
  icon,
  disabled = false,
  type = "button",
  fullWidth = false,
}) {
  const s = BTN_STYLES[variant] ?? BTN_STYLES.primary;
  const isSmall = size === "small";

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-all whitespace-nowrap"
      style={{
        padding: isSmall ? "0.3rem 0.75rem" : "0.5rem 1.125rem",
        height: isSmall ? 32 : 40,
        fontSize: isSmall ? "0.75rem" : "0.875rem",
        background: s.bg,
        color: s.text,
        border: `1px solid ${s.border}`,
        width: fullWidth ? "100%" : undefined,
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.background = s.hover;
      }}
      onMouseLeave={(e) => {
        if (!disabled) e.currentTarget.style.background = s.bg;
      }}
    >
      {icon && <i className={`${icon} ${isSmall ? "text-[10px]" : "text-xs"}`} />}
      {children}
    </button>
  );
}

/* ─── EditButton ────────────────────────────────────────────────── */
export function EditButton({ onClick, label = "Edit" }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-md text-[0.75rem] font-semibold transition-colors justify-center"
      style={{
        height: 32,
        padding: "0.3rem 0.75rem",
        background: "#EEF2FF",
        color: "#4F46E5",
        border: "1px solid #C7D2FE",
        minWidth: 64,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "#E0E7FF")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "#EEF2FF")}
    >
      <i className="fas fa-pencil" style={{ fontSize: 10 }} />
      {label}
    </button>
  );
}

/* ─── DeleteButton ──────────────────────────────────────────────── */
export function DeleteButton({ onClick, label = "Delete" }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-md text-[0.75rem] font-semibold transition-colors justify-center"
      style={{
        height: 32,
        padding: "0.3rem 0.75rem",
        background: "#FEF2F2",
        color: "#DC2626",
        border: "1px solid #FECACA",
        minWidth: 70,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "#FEE2E2")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "#FEF2F2")}
    >
      <i className="fas fa-trash" style={{ fontSize: 10 }} />
      {label}
    </button>
  );
}

/* ─── ViewButton ────────────────────────────────────────────────── */
export function ViewButton({ onClick, label = "View" }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-md text-[0.75rem] font-semibold transition-colors justify-center"
      style={{
        height: 32,
        padding: "0.3rem 0.75rem",
        background: "#F0FDF4",
        color: "#059669",
        border: "1px solid #BBF7D0",
        minWidth: 64,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "#DCFCE7")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "#F0FDF4")}
    >
      <i className="fas fa-eye" style={{ fontSize: 10 }} />
      {label}
    </button>
  );
}

/* ─── EmptyRow ──────────────────────────────────────────────────── */
export function EmptyRow({ message = "No records found", colSpan = 10 }) {
  return (
    <tr>
      <td colSpan={colSpan} className="py-16 text-center" style={{ color: "var(--color-text-muted)" }}>
        <i className="fas fa-inbox text-4xl mb-3 block" style={{ opacity: 0.25 }} />
        <p className="text-sm">{message}</p>
      </td>
    </tr>
  );
}

/* ─── TableCard ─────────────────────────────────────────────────── */
export function TableCard({ children }) {
  return <div className="page-card mb-4 sm:mb-6 overflow-hidden">{children}</div>;
}

/* ─── FormCard ──────────────────────────────────────────────────── */
export function FormCard({ children }) {
  return <div className="page-card mb-4 sm:mb-6">{children}</div>;
}

/* ─── PageTitle ─────────────────────────────────────────────────── */
export function PageTitle({ title, subtitle }) {
  return (
    <div className="px-4 py-3 sm:px-5 sm:py-4" style={{ borderBottom: "1px solid var(--color-border)" }}>
      <h2 className="text-sm sm:text-base font-bold" style={{ color: "var(--color-text-primary)" }}>{title}</h2>
      {subtitle && <p className="text-[10px] sm:text-xs mt-0.5" style={{ color: "var(--color-text-muted)" }}>{subtitle}</p>}
    </div>
  );
}
